<template>
  <div class="bg-white min-h-screen">
    <!-- Banner Section -->
    <section class="bg-gradient-to-br from-[#CB2530] to-[#B41923] relative overflow-hidden pt-24 lg:pt-32 pb-12 lg:pb-16 min-h-[320px] lg:min-h-[400px] flex items-center">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10 w-full">
        <h1 class="text-4xl md:text-5xl font-medium text-white mb-6 tracking-tight leading-tight">{{ $t('common.about.title') }}</h1>
      </div>
    </section>

    <!-- Company Introduction Section -->
    <section class="py-20 bg-white">
      <div class="container-custom">
        <h2 class="text-3xl text-slate-900 mb-6 tracking-tight font-medium">
          {{ $t('common.about.introTitle') }}
        </h2>
        <div :class="[$i18n.locale === 'en' ? 'text-left hyphens-auto' : 'text-justify', 'space-y-2 text-slate-700 leading-relaxed text-lg font-normal']">
          <p v-for="(paragraph, index) in introText.split('\n')" :key="index" v-if="paragraph.trim()" class="indent-[2em]">
            {{ paragraph }}
          </p>
        </div>
      </div>
    </section>

    <!-- Corporate Spirit Section -->
    <section class="py-20 bg-slate-50 overflow-hidden">
      <div class="container-custom">
        <h2 class="text-3xl text-slate-900 text-center mb-16 tracking-tight font-medium">{{ $t('common.about.spiritTitle') }}</h2>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 max-w-5xl mx-auto">
          <div 
            v-for="(item, index) in spirits.slice(0, 4)" 
            :key="index"
            class="bg-white p-8 rounded-2xl border border-slate-200 hover:border-[#C6202B]/40 hover:shadow-xl transition-all duration-500 group"
          >
            <div class="text-[#C6202B] mb-6 transition-transform duration-300 group-hover:scale-110">
              <img v-if="isExternalUrl(item.iconName)" :src="processImageUrl(item.iconName)" class="w-12 h-12 object-contain" />
              <i v-else :class="getIconClass(item.iconName)" class="text-4xl lg:text-5xl"></i>
            </div>
            <h3 class="text-xl text-slate-900 mb-4 font-medium tracking-tight">{{ getLocalizedField(item, 'title') }}</h3>
            <p class="text-slate-600 leading-relaxed font-normal">{{ getLocalizedField(item, 'description') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Certification Section -->
    <section class="py-20 bg-white">
      <div class="container-custom">
        <div class="text-center mb-12">
          <h2 class="text-3xl text-slate-900 mb-4 tracking-tight font-medium">{{ $t('common.about.honorTitle') }}</h2>
          <p class="text-slate-600 text-base">{{ $t('common.about.honorSubtitle') }}</p>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
          <div 
            v-for="(honor, index) in honors" 
            :key="index"
            class="flex items-center p-6 border border-slate-200 rounded-2xl hover:border-[#C6202B]/40 hover:shadow-xl transition-all duration-500 bg-white group gap-6"
          >
            <div class="w-16 h-16 flex-shrink-0 bg-[#FDF3F4] rounded-xl flex items-center justify-center border border-[#F5DDE0] overflow-hidden group-hover:bg-[#FAE7E9] transition-colors">
              <img v-if="isExternalUrl(honor.honorIcon)" :src="processImageUrl(honor.honorIcon)" class="h-10 w-auto object-contain group-hover:scale-110 transition-transform" />
              <template v-else><span class="text-xs font-bold text-[#C6202B] text-center px-1">{{ honor.honorIcon }}</span></template>
            </div>
            <div class="min-w-0">
              <h4 class="text-lg font-bold text-slate-900 group-hover:text-[#C6202B] transition-colors leading-tight tracking-tight">{{ getLocalizedField(honor, 'honorName') }}</h4>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { getAboutConfigs, getAboutSpirits, getAboutHonors } from '@/api/about'

export default {
  name: 'AboutView',
  props: {
    preloadedData: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      introConfig: {
        content: '',
        contentEn: '',
        image: require('../assets/images/about-company.png')
      },
      spirits: [],
      honors: []
    }
  },
  watch: {
    preloadedData: {
      handler(newVal) {
        if (newVal) {
          this.applyPreloadedData(newVal);
        }
      },
      immediate: true
    }
  },
  computed: {
    introText() {
      const isEn = this.$i18n && this.$i18n.locale === 'en'
      return isEn && this.introConfig.contentEn ? this.introConfig.contentEn : this.introConfig.content
    }
  },
  created() {
    this.fetchAboutData()
    this.$root.$on('language-changed', this.fetchAboutData)
  },
  beforeDestroy() {
    this.$root.$off('language-changed', this.fetchAboutData)
  },
  methods: {
    applyPreloadedData(data) {
      if (data.configs) {
        data.configs.forEach(item => {
          if (item.configKey === 'about_intro_content') this.introConfig.content = item.configValue || ''
          if (item.configKey === 'about_intro_content_en') this.introConfig.contentEn = item.configValue || ''
          if (item.configKey === 'about_intro_image') this.introConfig.image = this.processImageUrl(item.configValue)
        })
      }
      if (data.spirits && data.spirits.length > 0) this.spirits = data.spirits
      if (data.honors && data.honors.length > 0) this.honors = data.honors
    },
    async fetchAboutData() {
      try {
        const [configRes, spiritRes, honorRes] = await Promise.all([
          getAboutConfigs(),
          getAboutSpirits(),
          getAboutHonors()
        ])

        const freshData = {
          configs: configRes.data,
          spirits: spiritRes.data,
          honors: honorRes.data
        };
        this.applyPreloadedData(freshData);
      } catch (error) {
        console.error('Failed to fetch about data:', error)
      }
    },
    processImageUrl(url) {
      if (!url) return ''
      if (url.startsWith('http') || url.startsWith('blob:') || url.startsWith('data:')) return url
      const baseUrl = process.env.VUE_APP_BASE_API || 'http://localhost:8080'
      return baseUrl + url
    },
    isExternalUrl(url) {
      return url && (url.startsWith('/') || url.startsWith('http'))
    },
    getIconClass(name) {
      const iconMap = {
        Target: 'el-icon-bangzhu',
        TrendingUp: 'el-icon-data-line',
        Users: 'el-icon-user-solid',
        Cpu: 'el-icon-cpu',
        Handshake: 'el-icon-s-check',
        Leaf: 'el-icon-odometer',
        Factory: 'el-icon-office-building'
      }
      return iconMap[name] || 'el-icon-star-on'
    },
    getLocalizedField(item, field) {
      const isEn = this.$i18n && this.$i18n.locale === 'en'
      const localizedField = isEn ? `${field}En` : field
      return item[localizedField] || item[field] || ''
    }
  }
}
</script>

<style scoped>
.container-custom {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
}

/* 隐藏滚动条但保留滚动功能 */
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
